import { resetAIAnalystInitialized } from '@/app/ai/atoms/aiAnalystAtoms';
import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { fileViewState } from '@/app/fileViewState/fileViewState';
import { sheets } from '@/app/grid/controller/Sheets';
import { startupTimer } from '@/app/gridGL/helpers/startupTimer';
import { htmlCellsHandler } from '@/app/gridGL/HTMLGrid/htmlCells/htmlCellsHandler';
import { loadAssets } from '@/app/gridGL/loadAssets';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { thumbnail } from '@/app/gridGL/pixiApp/thumbnail';
import { evaluateFileSizeForDevice, fetchCheckpointSize, MOBILE_OOM_SESSION_FLAG } from '@/app/helpers/fileSizeLimits';
import { isNoMultiplayer } from '@/app/helpers/isEmbed';
import { isMobileMode } from '@/app/helpers/isMobile';
import { getPreloadState, preloadLanguageWorkers, preloadStateAtom, waitForPreload } from '@/app/preload/preload';
import initCoreClient from '@/app/quadratic-core/quadratic_core';
import { compareVersions, VersionComparisonResult } from '@/app/schemas/compareVersions';
import { showStockDataPanelAtom } from '@/app/ui/menus/AIAnalyst/stockDataPanelAtom';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { renderWebWorker } from '@/app/web-workers/renderWebWorker/renderWebWorker';
import { compileWasmModule } from '@/app/web-workers/wasmModule';
import { initCoreWorkers } from '@/app/web-workers/workers';
import { authClient } from '@/auth/auth';
import { scheduledTasksAtom } from '@/jotai/scheduledTasksAtom';
import { apiClient } from '@/shared/api/apiClient';
import { loadClientDataKv } from '@/shared/atom/clientDataKvAtom';
import { updateTeamBilling } from '@/shared/atom/teamBillingAtom';
import { ROUTES } from '@/shared/constants/routes';
import { registerEventAnalyticsData, trackEvent } from '@/shared/utils/analyticsEvents';
import { isAbortError, sendAnalyticsError } from '@/shared/utils/error';
import { trackOomDetected } from '@/shared/utils/oomAnalytics';
import { handleSentryReplays } from '@/shared/utils/sentry';
import { updateRecentFiles } from '@/shared/utils/updateRecentFiles';
import { captureEvent } from '@sentry/react';
import { atom, getDefaultStore } from 'jotai';
import { FilePermissionSchema, type ApiTypes } from 'quadratic-shared/typesAndSchemas';

// Tracks the current load operation to detect stale completions
let currentLoadId = 0;

export type FileData = ApiTypes['/v0/files/:uuid.GET.response'];

export type FileLoadingStatus = 'empty' | 'loading' | 'loaded' | 'error';

export interface FileLoadingProgress {
  loaded: number;
  total: number | null;
}

export interface FileLoadingState {
  uuid: string | null;
  status: FileLoadingStatus;
  data: FileData | null;
  error: string | null;
  isVersionHistoryPreview: boolean;
  sequenceNumber: number | undefined;
}

const defaultFileLoadingState: FileLoadingState = {
  uuid: null,
  status: 'empty',
  data: null,
  error: null,
  isVersionHistoryPreview: false,
  sequenceNumber: undefined,
};

export const fileLoadingAtom = atom<FileLoadingState>(defaultFileLoadingState);

// Progress for the in-flight file fetch lives in its own atom so that the
// many progress updates that arrive while streaming the file body don't
// cascade re-renders to every consumer of `fileLoadingAtom` (TopBar,
// useFileRouteLoaderData, useFileLoadingDomMirror, etc.). Only the loading
// overlay subscribes to this.
export const fileLoadingProgressAtom = atom<FileLoadingProgress | null>(null);

/**
 * Tracks whether the initial post-checkpoint multiplayer sync has completed
 * for the currently loaded file. The .grid checkpoint stored in S3 can lag
 * behind the room's true sequence number — so anything that happened after
 * the checkpoint (formatting, edits, table renames, …) only lands on the
 * client *after* multiplayer's first EnterRoom catch-up cycle. If we tear
 * down the loading overlay the moment `quadraticCore.load()` resolves, the
 * user briefly sees the pre-replay state (e.g. the OLD table name flashes
 * before the rename op replays). Keeping the overlay up until this gate
 * flips true masks the replay window.
 *
 * Semantics:
 *   - `true`  = no replay wait pending (either the initial sync has fired,
 *               or this load doesn't have live multiplayer to wait for —
 *               embed / `noMultiplayer` mode, version-history preview).
 *   - `false` = a load is in flight or just completed, and we're waiting
 *               for the first `multiplayerSynced` event.
 *
 * Default is `true` so the dashboard / non-file routes don't accidentally
 * gate on a sync that will never come.
 */
export const initialMultiplayerSyncedAtom = atom<boolean>(true);

// Maximum time we'll wait for the post-checkpoint multiplayer sync before
// dismissing the overlay anyway. Catches the case where multiplayer can't
// connect (server down, network blocked, etc.) so the user is never
// stranded behind a permanent overlay. Sync normally lands in well under
// a second; this is purely a safety floor.
const MULTIPLAYER_SYNC_OVERLAY_TIMEOUT_MS = 5000;

// Install the listener exactly once at module load. `multiplayerSynced`
// fires after every server-applied transaction batch and after every ack —
// flipping an already-true atom is a no-op, so we don't need to track
// "first fire" explicitly. Cross-file edge case: if a stale
// `multiplayerSynced` from a previous file lands between
// `loadFile` resetting the atom to false and the new file's multiplayer
// connecting, the gate would flip true a few hundred ms early. That's
// already strictly better than today's behavior (overlay dismisses
// immediately after core load) and the safety timeout caps the worst case.
let _initialSyncListenerInstalled = false;
const installInitialSyncListener = () => {
  if (_initialSyncListenerInstalled) return;
  _initialSyncListenerInstalled = true;
  events.on('multiplayerSynced', () => {
    getDefaultStore().set(initialMultiplayerSyncedAtom, true);
  });
};

export interface LoadFileOptions {
  uuid: string;
  sequenceNumber?: number;
  updateBilling?: boolean;
  requestUrl?: string;
  /**
   * Optional `AbortSignal` from the route loader's `LoaderFunctionArgs.request`.
   * When react-router interrupts the navigation (user clicks another link mid-load,
   * hits back, etc.), this signal aborts so the in-flight `fetch`s reject with
   * `AbortError` instead of staying alive and either resolving into a discarded
   * result or — more commonly — failing later as a generic `TypeError: Failed to
   * fetch` once the connection is reaped, which we'd otherwise mis-report to
   * Sentry as a real transport failure.
   */
  signal?: AbortSignal;
}

/**
 * Discriminator codes surfaced by loadFile so route-level error handling can
 * distinguish structured failures (e.g. mobile memory guard tripped) from
 * generic load errors.
 */
export const LOAD_FILE_ERROR_CODES = {
  FileTooLargeForMobile: 'FileTooLargeForMobile',
  MobileOutOfMemory: 'MobileOutOfMemory',
} as const;

export type LoadFileErrorCode = (typeof LOAD_FILE_ERROR_CODES)[keyof typeof LOAD_FILE_ERROR_CODES];

/**
 * Discriminator that tells the route loader (and its ErrorBoundary) what
 * actually failed, so it can pick a meaningful HTTP status for the thrown
 * `Response` and avoid re-reporting an error that was already sent to
 * Sentry by the layer that originally caught it.
 *
 *  - `transport`: failure reaching our API (fetch rejected at the transport
 *    layer, ApiError from a non-2xx response, DNS / proxy / extension
 *    block, etc.). Already reported by `loadFile`'s outer catch.
 *  - `core`: API returned the file metadata fine, but loading the
 *    checkpoint into the WASM core failed (download truncated, schema
 *    mismatch, panic in `GridController::newFromFile`). Already reported
 *    by the core worker via `sendAnalyticsError('core', 'loadFile', e)`.
 *  - `unknown`: unexpected error path with no upstream report.
 */
export type LoadFileFailureKind = 'transport' | 'core' | 'unknown';

export interface LoadFileResult {
  success: boolean;
  redirectUrl?: string;
  error?: string;
  errorCode?: LoadFileErrorCode;
  sizeBytes?: number;
  limitBytes?: number;
  /**
   * Set on failure. Lets the route loader translate the result into a
   * `Response` with an appropriate HTTP status without losing the
   * original cause.
   */
  kind?: LoadFileFailureKind;
  /**
   * Underlying HTTP status when known (e.g. propagated from `ApiError.status`).
   * The route loader prefers this over `kind`'s default mapping when present.
   */
  status?: number;
}

/**
 * Loads a file into the application.
 * This handles all the initialization that was previously in the file route loader.
 */
export const loadFile = async (options: LoadFileOptions): Promise<LoadFileResult> => {
  const { uuid, sequenceNumber, updateBilling = false, requestUrl, signal } = options;
  const store = getDefaultStore();
  const currentState = store.get(fileLoadingAtom);

  const isVersionHistoryPreview = sequenceNumber !== undefined;

  // If the same file and version is already loaded, skip reloading
  if (
    currentState.uuid === uuid &&
    currentState.status === 'loaded' &&
    currentState.sequenceNumber === sequenceNumber
  ) {
    return { success: true };
  }

  // Increment load ID to track this specific load operation.
  // This allows us to detect when a newer load has started and abort stale operations.
  const thisLoadId = ++currentLoadId;

  // Helper to check if this load operation is still current
  const isStaleLoad = () => currentLoadId !== thisLoadId;

  // Determine if we need to reset:
  // 1. Switching to a different file (need full reset)
  // 2. Loading a version history preview of same file (need to disconnect multiplayer)
  const needsReset = currentState.uuid !== null && (currentState.uuid !== uuid || isVersionHistoryPreview);

  if (needsReset) {
    // Mark sheets uninitialized as soon as we commit to tearing down the old
    // file. During `newFromFile` the core sends `a1Context` *before*
    // `sheetsInfo` (see `js_new_from_file`), which triggers a cursor-position
    // cascade. If we leave `sheets.initialized` true until the new
    // `sheetsInfo` lands, that cascade queries core (already on the new file)
    // with the old file's stale sheet id, producing "Invalid sheet ID" errors.
    sheets.initialized = false;

    const { resetCodeEditorState } = await import('@/app/atoms/codeEditorAtom');
    resetCodeEditorState();
    resetAIAnalystInitialized();
    htmlCellsHandler.clearCache();
    pixiApp.resetForNewFile();
    renderWebWorker.reset();

    // Reset panel states that are file-specific (only when switching to a different file)
    if (currentState.uuid !== uuid) {
      const { fileHistoryAtom } = await import('@/app/atoms/fileHistoryAtom');
      store.set(fileHistoryAtom, { show: false, checkpoints: [], activeSequenceNum: null, isLoading: false });
      store.set(showStockDataPanelAtom, false);
      store.set(scheduledTasksAtom, (prev) => ({ ...prev, show: false, currentTaskId: null, tasks: [] }));
    }
    const { multiplayer } = await import('@/app/web-workers/multiplayerWebWorker/multiplayer');
    if (currentState.uuid !== uuid) {
      // Use switchFile() for graceful connection release - the worker stays alive
      // and pending transactions will be drained before the old connection closes
      multiplayer.switchFile();
    } else if (isVersionHistoryPreview) {
      multiplayer.disconnect();
    }
    const { pythonWebWorker } = await import('@/app/web-workers/pythonWebWorker/pythonWebWorker');
    const { javascriptWebWorker } = await import('@/app/web-workers/javascriptWebWorker/javascriptWebWorker');
    pythonWebWorker.restart();
    javascriptWebWorker.restart();
    await quadraticCore.reset();
  }

  // Set loading state
  store.set(fileLoadingAtom, {
    uuid,
    status: 'loading',
    data: null,
    error: null,
    isVersionHistoryPreview,
    sequenceNumber,
  });
  store.set(fileLoadingProgressAtom, { loaded: 0, total: null });

  // Arm the multiplayer-sync gate so the overlay keeps masking the canvas
  // through the post-checkpoint replay window. We only wait for sync on
  // live multiplayer loads — embed (`noMultiplayer`) and version-history
  // previews never connect to a room, so there's no replay to wait for.
  // The listener is module-level and fires for the lifetime of the app; we
  // (re)install on every load as a no-op safeguard.
  installInitialSyncListener();
  const willHaveMultiplayer = !isNoMultiplayer && !isVersionHistoryPreview;
  store.set(initialMultiplayerSyncedAtom, !willHaveMultiplayer);

  startupTimer.reset();
  startupTimer.start('file.loader');

  // Load file data from API (can run in parallel with preload)
  const loadFileFromApi = async (): Promise<FileData | null> => {
    try {
      startupTimer.start('file.loader.files.get');
      const data = await apiClient.files.get(uuid, { updateBilling, signal, skipAuthRedirect: true });
      startupTimer.end('file.loader.files.get');

      if (debugFlag('debugShowMultiplayer') || debugFlag('debugShowFileIO')) {
        console.log(
          `[File API] Received information for file ${uuid} with sequence_num ${data.file.lastCheckpointSequenceNumber}.`
        );
      }
      return data;
    } catch (error: unknown) {
      const isLoggedIn = await authClient.isAuthenticated();
      const hasToken = isLoggedIn ? Boolean(await authClient.getTokenOrRedirect(true)) : false;
      const errorWithStatus = error as { status?: number };

      if (errorWithStatus.status === 403 && !hasToken) {
        // Need to redirect to login
        return null;
      }
      if (!isVersionHistoryPreview) {
        updateRecentFiles(uuid, '', false);
      }
      throw error;
    }
  };

  // Wait for preload and initialize any missing components
  const ensurePreloadComplete = async () => {
    // Wait for any in-progress preload to complete (should be fast since it started in loader)
    startupTimer.start('file.loader.waitForPreload');
    await waitForPreload();
    startupTimer.end('file.loader.waitForPreload');

    // Get the latest state after preload completes
    const preloadState = getPreloadState();

    // Initialize any components that weren't preloaded (fallback for edge cases)
    const initTasks: Promise<void>[] = [];

    if (!preloadState.assetsReady) {
      initTasks.push(
        (async () => {
          startupTimer.start('file.loader.loadPixi');
          try {
            await loadAssets();
          } catch (error) {
            sendAnalyticsError('file.loader', 'loadPixi', error, 'Error loading pixi assets');
          }
          startupTimer.end('file.loader.loadPixi');
        })()
      );
    }

    if (!preloadState.wasmReady) {
      initTasks.push(
        (async () => {
          startupTimer.start('file.loader.initCoreClient');
          const wasmModule = await compileWasmModule();
          await initCoreClient({ module_or_path: wasmModule });
          startupTimer.end('file.loader.initCoreClient');
        })()
      );
    }

    if (!preloadState.coreWorkersReady) {
      initCoreWorkers();
      store.set(preloadStateAtom, (prev) => ({ ...prev, coreWorkersReady: true }));
    }

    if (!preloadState.languageWorkersReady) {
      initTasks.push(preloadLanguageWorkers());
    }

    if (initTasks.length > 0) {
      await Promise.all(initTasks);
    }
  };

  try {
    // Run file API call and preload wait in parallel
    // The file API call is independent and can proceed while we wait for preload
    const [data] = await Promise.all([loadFileFromApi(), ensurePreloadComplete(), fileViewState.init(uuid)]);

    // Check if a newer load has started - if so, abort this one
    if (isStaleLoad()) {
      if (debugFlag('debugShowFileIO')) {
        console.log(`[loadFile] Aborting stale load for ${uuid}, newer load in progress`);
      }
      return { success: false, error: 'Load superseded by newer request' };
    }

    // If we got null, we need to redirect to login
    if (data === null) {
      store.set(fileLoadingAtom, defaultFileLoadingState);
      store.set(initialMultiplayerSyncedAtom, true);
      return {
        success: false,
        redirectUrl: requestUrl ? ROUTES.LOGIN_WITH_REDIRECT(requestUrl) : ROUTES.LOGIN,
      };
    }

    // Determine checkpoint to load
    let checkpoint = {
      url: data.file.lastCheckpointDataUrl,
      version: data.file.lastCheckpointVersion,
      sequenceNumber: data.file.lastCheckpointSequenceNumber,
    };

    if (isVersionHistoryPreview && sequenceNumber !== undefined) {
      const checkpointData = await apiClient.files.checkpoints.getBySequenceNumber(uuid, sequenceNumber, { signal });
      checkpoint = {
        url: checkpointData.dataUrl,
        version: checkpointData.version,
        sequenceNumber: checkpointData.sequenceNumber,
      };
    }

    // Mobile-memory pre-flight: issue a HEAD request against the checkpoint
    // URL to read Content-Length so we can refuse to load files that are
    // large enough to likely crash a mobile Safari tab (~300-500 MB limit).
    // HEAD failures fall through silently; the runtime OOM handler is the
    // safety net.
    const onMobile = isMobileMode();
    if (onMobile) {
      const stickyOom =
        typeof window !== 'undefined' && window.sessionStorage?.getItem(MOBILE_OOM_SESSION_FLAG) === 'true';
      if (stickyOom) {
        window.sessionStorage?.removeItem(MOBILE_OOM_SESSION_FLAG);
        trackOomDetected({ surface: 'sticky', isMobile: true, uuid });
        store.set(fileLoadingAtom, {
          uuid,
          status: 'error',
          data: null,
          error: 'Previous session ran out of memory',
          isVersionHistoryPreview,
          sequenceNumber,
        });
        return {
          success: false,
          error: 'Previous session ran out of memory',
          errorCode: LOAD_FILE_ERROR_CODES.MobileOutOfMemory,
        };
      }

      const sizeResult = await fetchCheckpointSize(checkpoint.url);
      if ('sizeBytes' in sizeResult) {
        const evaluation = evaluateFileSizeForDevice(sizeResult.sizeBytes, true);
        trackEvent('[loadFile].preflightSize', {
          uuid,
          sizeBytes: sizeResult.sizeBytes,
          status: evaluation.status,
          isMobile: true,
        });
        if (evaluation.status === 'block') {
          store.set(fileLoadingAtom, {
            uuid,
            status: 'error',
            data: null,
            error: 'File too large for mobile browser',
            isVersionHistoryPreview,
            sequenceNumber,
          });
          return {
            success: false,
            error: 'File too large for mobile browser',
            errorCode: LOAD_FILE_ERROR_CODES.FileTooLargeForMobile,
            sizeBytes: evaluation.sizeBytes,
            limitBytes: evaluation.limitBytes,
          };
        }
      } else {
        trackEvent('[loadFile].preflightSizeUnknown', { uuid, error: String(sizeResult.error) });
      }
    }

    // Load into core
    startupTimer.start('file.loader.quadraticCore.load');
    const result = await quadraticCore.load({
      fileId: uuid,
      teamUuid: data.team.uuid,
      url: checkpoint.url,
      version: checkpoint.version,
      sequenceNumber: checkpoint.sequenceNumber,
      noMultiplayer: isNoMultiplayer,
    });
    startupTimer.end('file.loader.quadraticCore.load');

    // Check again after core load - this is another async operation where switching could occur
    if (isStaleLoad()) {
      if (debugFlag('debugShowFileIO')) {
        console.log(`[loadFile] Aborting stale load for ${uuid} after core load, newer load in progress`);
      }
      return { success: false, error: 'Load superseded by newer request' };
    }

    if (result.error) {
      if (!isVersionHistoryPreview) {
        updateRecentFiles(uuid, data.file.name, false);
      }
      store.set(fileLoadingAtom, {
        uuid,
        status: 'error',
        data: null,
        error: result.error,
        isVersionHistoryPreview,
        sequenceNumber,
      });
      store.set(fileLoadingProgressAtom, null);
      // Open the multiplayer-sync gate on the error path so the error
      // page isn't masked by the loading overlay waiting for a sync that
      // will never happen.
      store.set(initialMultiplayerSyncedAtom, true);
      // The core worker already emitted a Sentry event with the original
      // Error (see `sendAnalyticsError('core', 'loadFile', e)` in
      // `quadraticCore/worker/core.ts`), so we don't re-report here. The
      // route loader translates this `kind` into status 500 and the
      // ErrorBoundary's 500 branch knows not to report.
      return { success: false, error: result.error, kind: 'core' };
    }

    if (result.version) {
      // Check version compatibility
      if (compareVersions(result.version, data.file.lastCheckpointVersion) === VersionComparisonResult.LessThan) {
        if (!isVersionHistoryPreview) {
          captureEvent({
            message: `User opened a file at version ${result.version} but the app is at version ${data.file.lastCheckpointVersion}. The app will automatically reload.`,
            level: 'log',
          });
          updateRecentFiles(uuid, data.file.name, false);
        }
        window.location.reload();
        return { success: false };
      }

      // Set thumbnail dirty if needed
      if (
        !isVersionHistoryPreview &&
        !data.file.thumbnail &&
        data.userMakingRequest.filePermissions.includes('FILE_EDIT')
      ) {
        thumbnail.setThumbnailDirty();
      }
    }

    // Give multiplayer a head start: begin connecting to the room as soon as
    // the grid is loaded, so the websocket EnterRoom round-trip overlaps the
    // remaining load work and the React mount of the file UI. The loading
    // overlay is held until the first post-checkpoint multiplayer sync (see
    // `initialMultiplayerSyncedAtom`); without this head start the connection
    // only begins once QuadraticApp mounts (well after the route commits),
    // leaving the overlay up for the full connect round-trip even on files
    // that are already fully synced. Fire-and-forget: `multiplayer.init` is
    // idempotent, and QuadraticApp re-issues it to cover the anonymous→login
    // transition, version-history previews, and pixiApp initialization.
    if (willHaveMultiplayer) {
      void (async () => {
        const user = await authClient.user();
        // Anonymous (logged-out) users can't authenticate against multiplayer;
        // QuadraticApp skips the connection for them too.
        if (!user || isStaleLoad()) return;
        const { multiplayer } = await import('@/app/web-workers/multiplayerWebWorker/multiplayer');
        if (isStaleLoad()) return;
        await multiplayer.init(uuid, user, false);
      })().catch(() => {
        // Best-effort head start. `multiplayer.init` clears its `connecting`
        // guard if it throws, so QuadraticApp's mount effect can retry the
        // connection.
      });
    }

    // Update recent files
    if (!isVersionHistoryPreview) {
      updateRecentFiles(uuid, data.file.name, true);
    }

    // Prepare final data with permission modifications
    let finalData = { ...data };

    // Make read-only for version history preview
    if (isVersionHistoryPreview) {
      finalData = {
        ...finalData,
        userMakingRequest: {
          ...finalData.userMakingRequest,
          filePermissions: [FilePermissionSchema.enum.FILE_VIEW],
        },
      };
    }

    // When noMultiplayer is enabled, allow editing since changes are local only
    if (isNoMultiplayer && !finalData.userMakingRequest.filePermissions.includes(FilePermissionSchema.enum.FILE_EDIT)) {
      finalData = {
        ...finalData,
        userMakingRequest: {
          ...finalData.userMakingRequest,
          filePermissions: [...finalData.userMakingRequest.filePermissions, FilePermissionSchema.enum.FILE_EDIT],
        },
      };
    }

    // Set up analytics and billing
    registerEventAnalyticsData({ isOnPaidPlan: finalData.team.isOnPaidPlan });
    updateTeamBilling({
      isOnPaidPlan: finalData.team.isOnPaidPlan,
      planType: finalData.team.planType ?? 'FREE',
    });
    handleSentryReplays(finalData.team.settings.analyticsAi);

    // Seed the user's clientDataKv into its dedicated atom (no-op if already
    // loaded in this session, e.g. when coming from the dashboard).
    if (finalData.userMakingRequest.id !== undefined) {
      await loadClientDataKv();
    }

    startupTimer.end('file.loader');
    startupTimer.show();

    // Set loaded state
    store.set(fileLoadingAtom, {
      uuid,
      status: 'loaded',
      data: finalData,
      error: null,
      isVersionHistoryPreview,
      sequenceNumber,
    });
    store.set(fileLoadingProgressAtom, null);

    // If the multiplayer-sync gate is still armed, schedule a safety
    // timeout so a failed multiplayer connection (server down, blocked
    // websocket, etc.) can't strand the user behind a permanent overlay.
    // Uses `thisLoadId` to ignore late fires from stale loads.
    if (willHaveMultiplayer) {
      setTimeout(() => {
        if (currentLoadId !== thisLoadId) return;
        store.set(initialMultiplayerSyncedAtom, true);
      }, MULTIPLAYER_SYNC_OVERLAY_TIMEOUT_MS);
    }

    return { success: true };
  } catch (error: unknown) {
    // Two cases where the failure isn't real and we shouldn't touch state or
    // notify Sentry:
    //   1. The user navigated away mid-load (newer load already in flight, or
    //      react-router aborted this loader's `request.signal`).
    //   2. The fetch was explicitly aborted (AbortError) — same root cause as
    //      (1) when `signal` is wired through, but also covers any future
    //      caller that aborts manually.
    // Both paths previously sent a noisy `Failed to fetch (host)` to Sentry
    // for a navigation the user already moved on from; the success paths
    // already bail on `isStaleLoad()`, so mirror that here.
    if (isStaleLoad() || isAbortError(error) || signal?.aborted) {
      if (debugFlag('debugShowFileIO')) {
        console.log(`[loadFile] Aborting stale/cancelled load for ${uuid}`, error);
      }
      return { success: false, error: 'Load superseded by newer request' };
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error loading file';
    const status = (error as { status?: unknown })?.status;
    const propagatedStatus = typeof status === 'number' ? status : undefined;

    store.set(fileLoadingAtom, {
      uuid,
      status: 'error',
      data: null,
      error: errorMessage,
      isVersionHistoryPreview,
      sequenceNumber,
    });
    store.set(fileLoadingProgressAtom, null);
    // Open the multiplayer-sync gate so the error UI isn't masked by the
    // overlay (which would otherwise wait for a sync that never comes).
    store.set(initialMultiplayerSyncedAtom, true);

    // Report once here with the real underlying Error so Sentry sees the
    // ApiError / TypeError / etc. with its actual message and stack. The
    // route loader rethrows as a `Response`, but the ErrorBoundary skips
    // re-reporting (its 503/500 branches both pass `error={undefined}` to
    // `EmptyPage`) so we don't end up with duplicate events.
    sendAnalyticsError('file.loader', 'loadFile', error, `Loading file ${uuid}`);

    return {
      success: false,
      error: errorMessage,
      kind: 'transport',
      status: propagatedStatus,
    };
  }
};

/**
 * Updates the progress of the in-flight file load. No-op if the atom is not
 * currently in the 'loading' state (e.g. progress event arrives after the
 * load completed or was superseded).
 */
export const setFileLoadingProgress = (loaded: number, total: number | null): void => {
  const store = getDefaultStore();
  if (store.get(fileLoadingAtom).status !== 'loading') return;
  store.set(fileLoadingProgressAtom, { loaded, total });
};

/**
 * Gets the current file loading state.
 */
export const getFileLoadingState = (): FileLoadingState => {
  return getDefaultStore().get(fileLoadingAtom);
};

/**
 * Checks if a specific file is already loaded.
 */
export const isFileLoaded = (uuid: string): boolean => {
  const state = getFileLoadingState();
  return state.uuid === uuid && state.status === 'loaded';
};

/**
 * Updates the cached file name for a loaded file. Used when the file is
 * renamed outside the editor (e.g. from the dashboard) so the warm
 * `fileLoadingAtom` snapshot — which the file route loader reuses without
 * refetching when returning to the same UUID — doesn't keep serving the old
 * name to the breadcrumb, document title, and other consumers.
 */
export const updateFileLoadingName = (uuid: string, name: string): void => {
  const store = getDefaultStore();
  const state = store.get(fileLoadingAtom);
  if (state.uuid !== uuid || !state.data || state.data.file.name === name) return;
  store.set(fileLoadingAtom, {
    ...state,
    data: { ...state.data, file: { ...state.data.file, name } },
  });
};

/**
 * Resets the file loading state to empty.
 * Called when unloading a file (e.g., navigating away).
 */
export const resetFileLoadingState = (): void => {
  const store = getDefaultStore();
  store.set(fileLoadingAtom, defaultFileLoadingState);
  store.set(fileLoadingProgressAtom, null);
  // Open the multiplayer-sync gate so subsequent non-file routes (e.g.
  // dashboard) don't see a stuck overlay.
  store.set(initialMultiplayerSyncedAtom, true);
};

/**
 * Loads a specific version of the currently loaded file, or reloads the
 * current version when called without a sequenceNumber.
 */
export const loadFileVersion = (sequenceNumber?: number): Promise<LoadFileResult> => {
  const state = getFileLoadingState();
  if (!state.uuid) {
    return Promise.resolve({ success: false, error: 'No file loaded' });
  }
  return loadFile({ uuid: state.uuid, sequenceNumber });
};
